import {isFeatureEnabled} from 'feature-flags';
import {Alert} from 'platform/components';
import {Show} from 'platform/foundation';
import {Lightbox} from 'platform/lightbox';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetCommentsQuery} from '@dms/api/core';
import {useGetAdditionalNoteQuery, useGetVehicleV2Query} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {
  useGetRentalVehicleQuery,
  useGetSaleVehicleQuery,
  useGetVehicleLocalActionNoteQuery,
} from '@dms/api/vehicle';
import {useGetServiceVehicleQuery} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import {testIds} from '@dms/routes';
import {
  EntityHeader,
  useIsLicenceEnabled,
  useTenant,
  useVehicleConditionEvaluation,
  useVehicleHeaderImage,
} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useGetVehicleControls} from '../hooks/useGetVehicleControls';
import {useGetVehicleHeaderActions} from '../hooks/useGetVehicleHeaderActions';
import {useGetVehicleHeaderActionsV3} from '../hooks/useGetVehicleHeaderActionsV3';
import {useGetVehicleHeaderFlags} from '../hooks/useGetVehicleHeaderFlags';
import {useGetVehicleHeaderInformationParameters} from '../hooks/useGetVehicleHeaderInformationParameters';
import {useGetVehicleHeaderParameters} from '../hooks/useGetVehicleHeaderParameters';
import {useGetVehicleHeaderPermissions} from '../hooks/useGetVehicleHeaderPermissions';
import {useGetVehicleSnippets} from '../hooks/useGetVehicleSnippets';
import {getRegistrationAlertData} from '../utils/getRegistrationAlertData';
import {TechnicalInspectionAlert} from './TechnicalInspectionAlert';

interface VehicleHeaderProps extends TestIdProps {
  vehicleId: string;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function VehicleHeader(props: VehicleHeaderProps) {
  const formatDateTime = useDateTimeFormatter();
  const {tenantCountry} = useTenant();

  const {isWorkshopModuleEnabled} = useIsLicenceEnabled();

  const {data: vehicle} = useGetVehicleV2Query({vehicleId: props.vehicleId});
  const {data: saleVehicle} = useGetSaleVehicleQuery({vehicleId: props.vehicleId});
  const {data: rentalVehicle} = useGetRentalVehicleQuery(
    {vehicleId: props.vehicleId},
    {skip: !isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3)}
  );
  const {data: serviceVehicle, isError: isServiceVehicleError} = useGetServiceVehicleQuery({
    vehicleId: props.vehicleId,
  });
  const {data: vehicleLocalActionNote} = useGetVehicleLocalActionNoteQuery(
    {vehicleId: props.vehicleId},
    {skip: !isWorkshopModuleEnabled}
  );

  const {data: comments = []} = useGetCommentsQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: props.vehicleId,
  });

  const {data: additionalNote} = useGetAdditionalNoteQuery({vehicleId: props.vehicleId});

  const hasComments = comments.length > 0;

  const isSaleVehicle = !!saleVehicle;
  // RTK Query keeps the last successful data through a failed refetch, so the error has to be
  // checked too — otherwise the header disagrees with VehicleDetail, which hides the service
  // sections on exactly that signal.
  const isServiceVehicle = isNotNil(serviceVehicle) && !isServiceVehicleError;

  const actionsV2 = useGetVehicleHeaderActions(props.vehicleId);
  const actionsV3 = useGetVehicleHeaderActionsV3(props.vehicleId);

  const actions = isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3) ? actionsV3 : actionsV2;

  const {primaryParameter, secondaryParameter, tertiaryParameter, additionalParameterBlocks} =
    useGetVehicleHeaderParameters(saleVehicle, rentalVehicle);

  const vehicleHeaderParameters = useGetVehicleHeaderInformationParameters(
    isSaleVehicle,
    isServiceVehicle,
    vehicle,
    hasComments,
    additionalNote?.note
  );

  const flags = useGetVehicleHeaderFlags(props.vehicleId);
  const {snippets, isLoading} = useGetVehicleSnippets(props.vehicleId);
  const [vehicleConditionEvaluation, {isLoading: isAuditLoading}] = useVehicleConditionEvaluation(
    props.vehicleId
  );
  const [lightboxControls, coverPhoto, onCoverPhotoOpen, vehicleImages, photoDownloadSizeDialog] =
    useVehicleHeaderImage(props.vehicleId);

  const {
    hasVehicleTagReadPermission,
    hasVehicleTagCreatePermission,
    hasVehicleTagUpdatePermission,
    hasVehicleTagDeletePermission,
    hasVehicleTagAssignPermission,
    hasVehicleTagUnassignPermission,
    canChangeVehicleOwner,
    canAssignVehicleUser,
    canUnassignVehicleUser,
    canSeeVehiclePrice,
  } = useGetVehicleHeaderPermissions({
    vehicleId: props.vehicleId,
    saleVehicle,
  });

  const controls = useGetVehicleControls({
    isSaleVehicle,
    isServiceVehicle,
    vehicleId: props.vehicleId,
    saleVehicle,
    branchId: saleVehicle?.branchId,
  });

  const recallMessage = vehicleLocalActionNote?.local_action_note_text;
  const wasSold = saleVehicle && isNilOrEmpty(saleVehicle.soldAt);

  const shouldShowRegistrationWarning =
    tenantCountry === 'POL' &&
    saleVehicle?.type === 'IN_STOCK' &&
    vehicle?.condition !== 'CONDITION_NEW' &&
    !vehicle?.isRegistered &&
    wasSold;

  const shouldShowVehicleCondition =
    (isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3) &&
      saleVehicle &&
      saleVehicle.sellingState !== 'not-for-sale') ||
    (!isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3) &&
      (isSaleVehicle || !isWorkshopModuleEnabled));

  const shouldShowVehiclePrice =
    (isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3) &&
      saleVehicle &&
      saleVehicle.sellingState !== 'not-for-sale' &&
      canSeeVehiclePrice) ||
    (!isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3) && canSeeVehiclePrice);

  const registrationAlertData =
    shouldShowRegistrationWarning && saleVehicle?.purchaseDate
      ? getRegistrationAlertData({purchaseDateAt: saleVehicle.purchaseDate, formatDateTime})
      : null;

  return (
    <>
      <EntityHeader
        title={vehicle?.title}
        imageVariant="image"
        icon={isNil(coverPhoto) ? 'maps/directions_car' : undefined}
        image={coverPhoto}
        onImageClick={coverPhoto ? onCoverPhotoOpen : undefined}
        resourceId={EntityResourceIds.vehicle}
        enableTeams={isFeatureEnabled(featureFlags.CORE_TEAMS)}
        additionalParameterBlocks={
          isFeatureEnabled(featureFlags.VEHICLE_HEADER_V3) ? additionalParameterBlocks : undefined
        }
        primaryParameter={shouldShowVehiclePrice ? primaryParameter : undefined}
        secondaryParameter={shouldShowVehiclePrice ? secondaryParameter : undefined}
        tertiaryParameter={shouldShowVehiclePrice ? tertiaryParameter : undefined}
        flags={flags}
        parameters={vehicleHeaderParameters}
        actions={actions}
        recordId={props.vehicleId}
        snippets={snippets}
        isLoadingSnippets={isLoading || isAuditLoading}
        controls={controls}
        vehicleHistory={{
          vehicleId: props.vehicleId,
          serviceRequestsGridCode: 'vehicle-history-order-requests',
          workItemsGridCode: 'vehicle-history-order-items',
        }}
        vehicleConditionEvaluation={
          shouldShowVehicleCondition ? vehicleConditionEvaluation : undefined
        }
        data-testid={suffixTestId('vehicleHeader', props)}
        permissions={{
          tag: {
            isReadable: hasVehicleTagReadPermission,
            isCreatable: hasVehicleTagCreatePermission,
            isUpdatable: hasVehicleTagUpdatePermission,
            isDeletable: hasVehicleTagDeletePermission,
            isAssignable: hasVehicleTagAssignPermission,
            isUnassignable: hasVehicleTagUnassignPermission,
          },
          participation: {
            canAssignUser: canAssignVehicleUser,
            canChangeOwner: canChangeVehicleOwner,
            canUnassignUser: canUnassignVehicleUser,
          },
        }}
        alerts={
          !isSaleVehicle && recallMessage ? [{type: 'banner', message: recallMessage}] : undefined
        }
      />
      <Lightbox
        data-testid={testIds.vehicles.detail('header')}
        controls={lightboxControls}
        images={vehicleImages}
      />
      {photoDownloadSizeDialog}
      <TechnicalInspectionAlert date={vehicle?.technicalInspectionValidUntil} />
      <Show when={shouldShowRegistrationWarning && registrationAlertData}>
        <Alert
          variant={registrationAlertData?.variant}
          title={registrationAlertData?.title}
          message={registrationAlertData?.description}
        />
      </Show>
    </>
  );
}
